// src/rms/data/agreementDocumentService.js
// ============================================================================
// Freezing a contract onto one booking, and signing it
// ============================================================================
// agreementService.js is about what the operator's contract SAYS. This is about
// what one customer was shown and agreed to, at one moment, in the words that
// were in force then.
//
// ----------------------------------------------------------------------------
// Resolve once, at the moment of signing. Never at render time.
// ----------------------------------------------------------------------------
// Every {{token}} is substituted here and the result is stored whole. Nothing
// downstream substitutes anything: the printable page, the PDF and the emailed
// copy all lay out the same stored characters.
//
// That is what makes the stored hash worth having, and it is also what stops a
// customer's address being silently rewritten on a contract they signed last
// month because somebody corrected their record last week.
//
// ----------------------------------------------------------------------------
// The hash
// ----------------------------------------------------------------------------
// Computed here for the local backend, and RECOMPUTED by the database on every
// write (rms_agreement_document_freeze, 20260821170000). Whatever this module
// sends is discarded on Supabase, which is the point -- a hash chosen by the
// writer proves nothing about the text it claims to cover. The two must agree,
// so both hash the UTF-8 bytes of body_snapshot and nothing else: no trimming,
// no normalising, no BOM.
// ============================================================================

import db, { isLocalBackend } from "./index";
import { supabase } from "../config/supabaseClient";
import pinService from "./pinService";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import agreementService from "./agreementService";
import { tokenValues, renderBody, blanksIn } from "../agreements/resolve";
import { unknownTokens } from "../agreements/tokens";

/**
 * SHA-256 of a string, lowercase hex.
 *
 * crypto.subtle needs a secure context; localhost counts, and so does every
 * deployment. Returns "" rather than throwing where it is unavailable, because
 * on Supabase the database's value wins anyway and a missing hash must not stop
 * a customer signing at a counter.
 */
export async function sha256Hex(text) {
  try {
    const bytes = new TextEncoder().encode(String(text ?? ""));
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
}

// ----------------------------------------------------------------------------
// Rendering
// ----------------------------------------------------------------------------

/**
 * Every active document for this booking, rendered.
 *
 * Returns previews -- nothing is written. The checkout screen shows these so
 * the customer reads the real thing, and passes the same array back to
 * signAtCounter, so what was displayed and what is stored cannot differ.
 *
 * @returns {Promise<{success, data: Array<{docKey, kind, locale, title, body, templateId, unresolved}>}>}
 */
export async function renderForBooking(operator, ctx) {
  const { customer, asset, reservation, location, quote, formatMoney } = ctx;

  const templates = await agreementService.listTemplates(operator.id);
  if (!templates.success) return templates;

  const values = tokenValues({ operator, customer, asset, reservation, location, quote, formatMoney });
  const grouped = agreementService.groupByDocument(templates.data || []);

  const out = [];
  for (const group of grouped) {
    // One row per document, in the customer's language where the operator has
    // written one. rms_customers.locale means "what language do we write to
    // this person in" (20260817160000) -- so it decides this, not the staff
    // member's own UI language.
    const row = agreementService.bodyForLocale(group.locales, {
      locale: customer?.locale,
      operatorLocale: operator?.default_locale,
    });
    if (!row) continue;

    const body = renderBody(row.body, values);
    out.push({
      docKey: row.doc_key,
      kind: row.kind,
      locale: row.locale,
      title: renderBody(row.title, values),
      body,
      templateId: row.id,
      // Both reported, neither refused: the counter must not be blocked by
      // something only the owner can fix, but staff should see it before a
      // customer signs a page of ruled lines.
      //
      //   blanks  -- a token we know but have no value for. Printed as a blank.
      //             Usually the operator has not filled in Settings yet.
      //   unknown -- a token this app has never heard of. Printed literally,
      //             because we have nothing to put there and guessing would be
      //             worse. That one is a typo in the template.
      blanks: blanksIn(row.body, values),
      unknown: unknownTokens(row.body),
    });
  }

  // The agreement first, then waivers, then addenda -- the order a counter
  // hands them over in and the order they are signed. doc_key alphabetical, as
  // the rows arrive, would put "additional_driver" above the contract it is an
  // addendum to.
  const RANK = { agreement: 0, waiver: 1, addendum: 2 };
  out.sort((a, b) => (RANK[a.kind] ?? 9) - (RANK[b.kind] ?? 9) || a.docKey.localeCompare(b.docKey));

  return createSuccessResponse(out);
}

// ----------------------------------------------------------------------------
// Signing
// ----------------------------------------------------------------------------

/**
 * Freeze and sign every prepared document, at the counter.
 *
 * One signature ceremony covering several enumerated documents, which is how a
 * counter actually works: the customer reads the agreement and the waiver, ticks
 * once, signs once. Each document still gets its own row, its own frozen text
 * and its own hash -- so "what did they sign" has a per-document answer even
 * though the pen moved once.
 *
 * consentedAt is passed in separately from the signature rather than defaulted
 * to now(). They are two acts -- agreeing to sign electronically, then signing --
 * and ESIGN and UETA care about the first as its own thing.
 *
 * No staff PIN. This runs inside a checkout whose agreement.open signature was
 * already minted and is already bound to the write; a second prompt inside one
 * gesture at the desk is the thing 20260821130000 argues against for invoices.
 */
export async function signAtCounter(operator, prepared, opts) {
  const {
    customerId,
    reservationId,
    signerName,
    signerEmail,
    signaturePath,
    consentedAt,
    memberId,
  } = opts;

  if (!operator?.id) return createErrorResponse("No business loaded.");
  if (!prepared?.length) return createSuccessResponse([]);
  if (!customerId) return createErrorResponse("A contract needs somebody to sign it.");
  if (!String(signerName || "").trim()) {
    return createErrorResponse("Type the name of the person signing.");
  }
  if (!consentedAt) {
    return createErrorResponse("The customer has to agree to sign electronically first.");
  }

  // The PNG is uploaded by the caller and referenced by path from every
  // document here, and from rms_agreements.checkout_signature_path -- one
  // stored image, several rows pointing at it, rather than a copy per row.
  const signedAt = new Date().toISOString();
  const created = [];

  for (const doc of prepared) {
    const row = await db.create("agreementDocuments", {
      operator_id: operator.id,
      reservation_id: reservationId || null,
      customer_id: customerId,
      template_id: doc.templateId || null,
      doc_key: doc.docKey,
      kind: doc.kind,
      locale: doc.locale,
      title: doc.title,
      body_snapshot: doc.body,
      body_sha256: await sha256Hex(doc.body),
      status: "signed",
      prepared_by: memberId || null,
      signed_at: signedAt,
      signer_name: String(signerName).trim(),
      signer_email: signerEmail || null,
      signer_consented_at: consentedAt,
      signed_via: "counter",
      signature_storage_path: signaturePath || null,
    });
    // Stop on the first refusal rather than pressing on. A customer who signed
    // the agreement but not the waiver, because the second insert failed, is a
    // worse state than a checkout that stopped and said so.
    if (!row.success) return row;
    created.push(row.data);
  }

  return createSuccessResponse(created);
}

/**
 * Attach the agreement created at checkout to the documents signed for it.
 *
 * Separate from signAtCounter because the agreement row does not exist until
 * rentalService.checkOut has run, and that runs after -- the customer signs, then
 * the vehicle is released. The trigger allows this one column to arrive late on
 * a signed row precisely so this can happen.
 */
export async function attachAgreement(documents, agreementId) {
  if (!agreementId || !documents?.length) return createSuccessResponse(0);
  let attached = 0;
  for (const doc of documents) {
    const updated = await db.update("agreementDocuments", doc.id, {
      agreement_id: agreementId,
      updated_at: new Date().toISOString(),
    });
    if (updated.success) attached += 1;
  }
  return createSuccessResponse(attached);
}

// ----------------------------------------------------------------------------
// Reading
// ----------------------------------------------------------------------------

export async function listForReservation(operatorId, reservationId) {
  return db.list(
    "agreementDocuments",
    { operator_id: operatorId, reservation_id: reservationId },
    { orderBy: "created_at" }
  );
}

export async function listForOperator(operatorId) {
  return db.list("agreementDocuments", { operator_id: operatorId }, {
    orderBy: "created_at",
    direction: "desc",
  });
}

export async function getDocument(id) {
  return db.get("agreementDocuments", id);
}

/**
 * Does the stored text still hash to what the row says?
 *
 * On Supabase this can only fail if somebody edited the database outside the
 * app, which is exactly the question worth being able to answer. On the local
 * backend it is a self-check on this module.
 */
export async function verifyIntegrity(doc) {
  if (!doc) return createErrorResponse("No document.");
  const computed = await sha256Hex(doc.body_snapshot);
  if (!computed) return createSuccessResponse({ checked: false });
  return createSuccessResponse({
    checked: true,
    intact: computed === doc.body_sha256,
    computed,
    stored: doc.body_sha256,
  });
}

// ----------------------------------------------------------------------------
// Sending it to somebody who is not here
// ----------------------------------------------------------------------------
// Everything below has two implementations, and the local one is not a mock: it
// mints a token, hashes it, stores it in its own collection and resolves it the
// same way the SQL does. What it cannot do is send an email or hold a file, and
// it says so rather than pretending -- the same line messageService draws.

/** One uniform refusal, matching the SQL. Distinguishing expired from
 *  already-signed from never-existed turns the endpoint into an oracle. */
const BAD_LINK = "That link is not valid any more. Ask the business to send you a new one.";

const mintToken = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

/** A non-2xx from an edge function carries the real message in its body. */
async function readFunctionError(error) {
  try {
    const body = await error.context?.json();
    if (body?.error) return body.error;
  } catch {
    /* fall through to the generic message */
  }
  return error.message || "That could not be sent.";
}

/**
 * Freeze a document as a DRAFT, ready to be sent.
 *
 * Same freeze as signAtCounter and the same reason: what the customer opens in
 * a week must be what the contract said today, not what it says by then.
 */
export async function prepareForSignature(operator, prepared, { customerId, reservationId, memberId }) {
  if (!operator?.id) return createErrorResponse("No business loaded.");
  if (!prepared?.length) return createErrorResponse("There is nothing to send.");
  if (!customerId) return createErrorResponse("A contract needs somebody to sign it.");

  // Reuse a draft already sitting on this booking rather than adding another.
  //
  // Anything after this can fail -- the PIN, the email, the network -- and the
  // obvious thing to do next is press the button again. Without this, the third
  // attempt leaves nine abandoned drafts on one reservation, each one a frozen
  // contract nobody sent. Found exactly that way: one failed send, three
  // orphans.
  //
  // Only DRAFTS are reused. A document already sent or signed is a fact about
  // what happened and gets left alone.
  const existing = reservationId
    ? (await listForReservation(operator.id, reservationId)).data || []
    : [];
  const reusable = new Map(
    existing.filter((d) => d.status === "draft").map((d) => [d.doc_key, d])
  );

  // Anything already out or already signed is LEFT ALONE and not re-prepared.
  //
  // Without this, pressing "Send agreement" twice built a second rental
  // agreement, waiver and driver form on the same booking and mailed three more
  // links -- because only drafts were reused and a `sent` document matched
  // nothing. The reservation would read "1 signed, 5 awaiting" and no screen
  // said why. Resending is now its own action; sending is not a way to reach it
  // by accident.
  const settled = new Set(
    existing.filter((d) => d.status !== "draft").map((d) => d.doc_key)
  );

  const created = [];
  for (const doc of prepared) {
    if (settled.has(doc.docKey)) continue;
    const already = reusable.get(doc.docKey);
    if (already) {
      // Re-freeze: the template or the booking may have changed since the
      // abandoned attempt, and the draft should say what it says today.
      const updated = await db.update("agreementDocuments", already.id, {
        title: doc.title,
        body_snapshot: doc.body,
        body_sha256: await sha256Hex(doc.body),
        template_id: doc.templateId || null,
        updated_at: new Date().toISOString(),
      });
      if (!updated.success) return updated;
      created.push(updated.data || already);
      continue;
    }

    const row = await db.create("agreementDocuments", {
      operator_id: operator.id,
      reservation_id: reservationId || null,
      customer_id: customerId,
      template_id: doc.templateId || null,
      doc_key: doc.docKey,
      kind: doc.kind,
      locale: doc.locale,
      title: doc.title,
      body_snapshot: doc.body,
      body_sha256: await sha256Hex(doc.body),
      status: "draft",
      prepared_by: memberId || null,
    });
    if (!row.success) return row;
    created.push(row.data);
  }
  return createSuccessResponse(created);
}

/**
 * Mint a link and email it.
 *
 * PIN-signed, because emailing a contract is outward-facing and cannot be taken
 * back, and because on a shared counter the session cannot say who did it. The
 * signature is spent by the status change to `sent`, so a send that never left
 * leaves nothing in the log claiming it did.
 */
export async function sendForSignature(operator, document, { pin, days = 14, resend = false }) {
  if (!operator?.id || !document?.id) return createErrorResponse("No document chosen.");

  const alreadyOut = document.status === "sent" || document.status === "viewed";
  if (["signed", "declined", "void"].includes(document.status)) {
    return createErrorResponse("That document has already been signed.");
  }
  if (resend && !alreadyOut) {
    return createErrorResponse("That document has not been sent yet.");
  }
  if (!resend && alreadyOut) {
    return createErrorResponse("That document has already been sent. Send a new link instead.");
  }

  const signed = await pinService.signAction(operator, {
    pin,
    // Its own action, not a reuse of send. The trigger that promotes the
    // signature fires on draft -> sent, which a resend does not do, so reusing
    // the name would leave the signature unspent and the resend unattributed --
    // and "who mailed this out, and to where" is the whole question a resend
    // raises.
    action: resend ? "agreement_doc.resend" : "agreement_doc.send",
    subjectTable: "rms_agreement_documents",
    subjectId: document.id,
  });
  if (!signed.success) return signed;

  if (isLocalBackend) {
    const token = mintToken();
    // Upsert, matching rms_mint_sign_token: one live link per document, so
    // issuing a new one kills the last.
    const existingTokens = await db.list("agreementSignTokens", { document_id: document.id });
    for (const row of existingTokens.data || []) await db.remove("agreementSignTokens", row.id);
    await db.create("agreementSignTokens", {
      document_id: document.id,
      token_hash: await sha256Hex(token),
      expires_at: new Date(Date.now() + days * 864e5).toISOString(),
    });
    await db.update("agreementDocuments", document.id, {
      status: resend ? document.status : "sent",
      sent_at: resend ? document.sent_at : new Date().toISOString(),
      last_sent_at: new Date().toISOString(),
      resent_count: resend ? (document.resent_count || 0) + 1 : 0,
      sent_signature_id: signed.data,
      updated_at: new Date().toISOString(),
    });
    // The link, not a claim that it was emailed. inviteMember does the same,
    // and for the same reason: faking a send is worse than not sending.
    return createSuccessResponse({
      localOnly: true,
      link: `${window.location.origin}/sign/${token}`,
    });
  }

  const { data, error } = await supabase.functions.invoke("send-agreement", {
    body: { document_id: document.id, signature_id: signed.data, days, resend },
  });
  if (error) return createErrorResponse(await readFunctionError(error));
  if (data?.error) return createErrorResponse(data.error);
  return createSuccessResponse({ sentTo: data?.sent_to });
}

// ----------------------------------------------------------------------------
// The public side
// ----------------------------------------------------------------------------

async function localDocumentForToken(token, { signedOnly = false } = {}) {
  const hash = await sha256Hex(token || "");
  if (!hash) return null;

  if (signedOnly) {
    const all = await db.list("agreementDocuments", {});
    return (all.data || []).find((d) => d.view_token_hash === hash && d.status === "signed") || null;
  }

  const tokens = await db.list("agreementSignTokens", {});
  const row = (tokens.data || []).find((x) => x.token_hash === hash);
  if (!row || new Date(row.expires_at) < new Date()) return null;

  const doc = (await db.get("agreementDocuments", row.document_id)).data;
  if (!doc || !["draft", "sent", "viewed"].includes(doc.status)) return null;
  return doc;
}

/** Read the contract behind a signing link. */
export async function fetchForSigning(token) {
  if (!/^[a-f0-9]{64}$/i.test(String(token || ""))) return createErrorResponse(BAD_LINK);

  if (isLocalBackend) {
    const doc = await localDocumentForToken(token);
    if (!doc) return createErrorResponse(BAD_LINK);

    const operator = (await db.get("operators", doc.operator_id)).data;
    const customer = (await db.get("customers", doc.customer_id)).data;
    await db.update("agreementDocuments", doc.id, {
      status: doc.status === "draft" ? doc.status : "viewed",
      viewed_at: doc.viewed_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
    return createSuccessResponse({
      document_id: doc.id,
      title: doc.title,
      body: doc.body_snapshot,
      locale: doc.locale,
      fingerprint: doc.body_sha256,
      business_name: operator?.business_name || "",
      signer_name: [customer?.first_name, customer?.last_name].filter(Boolean).join(" "),
    });
  }

  const { data, error } = await supabase.functions.invoke("sign-agreement", {
    body: { action: "fetch", token },
  });
  if (error) return createErrorResponse(await readFunctionError(error));
  if (!data?.ok) return createErrorResponse(data?.error || BAD_LINK);
  return createSuccessResponse(data);
}

/** Sign it. */
export async function signRemotely(token, { signerName, consented, signatureDataUrl }) {
  const name = String(signerName || "").trim();
  if (!name) return createErrorResponse("Type your full name to sign.");
  if (!consented) return createErrorResponse("You have to agree to sign electronically first.");

  if (isLocalBackend) {
    const doc = await localDocumentForToken(token);
    if (!doc) return createErrorResponse(BAD_LINK);

    const viewToken = mintToken();
    const now = new Date().toISOString();
    // No IP and no user agent on this backend, and they are left NULL rather
    // than filled with something plausible. An audit trail with invented
    // entries is worse than one with gaps.
    const updated = await db.update("agreementDocuments", doc.id, {
      status: "signed",
      signed_at: now,
      signer_name: name,
      signer_consented_at: now,
      signed_via: "remote",
      view_token_hash: await sha256Hex(viewToken),
      updated_at: now,
    });
    if (!updated.success) return updated;

    const tokens = await db.list("agreementSignTokens", { document_id: doc.id });
    for (const row of tokens.data || []) await db.remove("agreementSignTokens", row.id);

    return createSuccessResponse({
      document_id: doc.id,
      view_token: viewToken,
      view_url: `${window.location.origin}/signed/${viewToken}`,
      localOnly: true,
    });
  }

  const { data, error } = await supabase.functions.invoke("sign-agreement", {
    body: { action: "sign", token, signer_name: name, consented: true, signature: signatureDataUrl },
  });
  if (error) return createErrorResponse(await readFunctionError(error));
  if (!data?.ok) return createErrorResponse(data?.error || BAD_LINK);
  return createSuccessResponse(data);
}

/** The permanent read-only copy. */
export async function viewSigned(token) {
  if (!/^[a-f0-9]{64}$/i.test(String(token || ""))) return createErrorResponse(BAD_LINK);

  if (isLocalBackend) {
    const doc = await localDocumentForToken(token, { signedOnly: true });
    if (!doc) return createErrorResponse(BAD_LINK);
    const operator = (await db.get("operators", doc.operator_id)).data;
    return createSuccessResponse({
      document_id: doc.id,
      title: doc.title,
      body: doc.body_snapshot,
      fingerprint: doc.body_sha256,
      signed_at: doc.signed_at,
      signer_name: doc.signer_name,
      consented_at: doc.signer_consented_at,
      signed_via: doc.signed_via,
      business_name: operator?.business_name || "",
    });
  }

  const { data, error } = await supabase.functions.invoke("sign-agreement", {
    body: { action: "view", token },
  });
  if (error) return createErrorResponse(await readFunctionError(error));
  if (!data?.ok) return createErrorResponse(data?.error || BAD_LINK);
  return createSuccessResponse(data);
}

export default {
  sha256Hex,
  prepareForSignature,
  sendForSignature,
  fetchForSigning,
  signRemotely,
  viewSigned,
  renderForBooking,
  signAtCounter,
  attachAgreement,
  listForReservation,
  listForOperator,
  getDocument,
  verifyIntegrity,
  isLocalBackend,
};
